import * as MuiIcons from "@mui/icons-material";
import { Euro, LibraryAddRounded } from "@mui/icons-material";
import * as Mui from '@mui/material';
import { Button, Grid, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { useContext, useEffect } from "react";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FormTextField from "../../../experiments/forms/FormTextField";
import { useCashBackRepository } from "../../../api/repositories/CashbackRepository";
import { fixThresholdValue, offsetDateJsConvert, reverseConvertDate } from "../util/CashbackUtil";
import { useRecoilState } from "recoil";
import { STATE_LIVEMONITORING_UPDATING } from "../constants/CashbackRecoil";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";

interface Props {
  open: boolean;
  currentRow: any;
  onClose: () => void;
}

const EditNote = (props: Props) => {

  const { t } = useTranslation('finance', { keyPrefix: 'cashback' });


  const schema = Yup.object().shape({
    notes: Yup.array().of(
      Yup.object().shape({
        amount: Yup.string().required(t('tables.livemonitoring.required-error')),
        date: Yup.string().nullable().required(t('tables.livemonitoring.required-error')),
        text: Yup.string().nullable().required(t('tables.livemonitoring.required-error'))
      })
    )
  });

  const { getLiveMonitoringNotes, postLiveMonitoringNotes } = useCashBackRepository();
  const { dispatchGlobalData } = useContext(GlobalContext);
  const [updating, setUpdating] = useRecoilState(STATE_LIVEMONITORING_UPDATING);
  const { handleSubmit, control, register } = useForm<any>({
    defaultValues: {

    },
    resolver: yupResolver(schema),
  });
  const { fields, append, remove,  insert} = useFieldArray({
    control, 
    name: "notes" 
  });
  
  async function renderNotes() {
    let liveMonitoringNotes = await getLiveMonitoringNotes(props.currentRow["sap-id"]);
    let notes: any[] = liveMonitoringNotes;
    notes.map((note: any) => {
      append({ amount: note.amount ? note.amount/100 : null , date: note.date, text: note.text })
    }); 
  }
  

  useEffect(() => {
    renderNotes();
  }, []);
 
  const onSubmit: SubmitHandler<any> = (data) => {
     data.notes.forEach((element: any) => {
      element['date'] = offsetDateJsConvert(reverseConvertDate(element['date']), 'DD/MM/YYYY') 
      element.amount = fixThresholdValue(element.amount);
      element['sap-id'] = props.currentRow["sap-id"];
    });
    let dataToPost = data.notes;
    postLiveMonitoringNotes(props.currentRow["sap-id"], dataToPost).then(() => {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('tables.livemonitoring.snackbar.success-update'),
            type: snackbarTypes.success,
            show: true,
            autoClose: true
          }
        }
      });
      onClose();
    }).catch(() => {
      dispatchGlobalData({
        type: setSnackbar,
        payload: {
          snackbar: {
            message: t('tables.livemonitoring.snackbar.error-update'),
            type: snackbarTypes.error,
            show: true,
            autoClose: true
          }
        }
      });
    });
    onClose(); 
  }

  const onClose = () => {
    props.onClose();
  };

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Mui.DialogTitle>
          <Mui.Grid container alignItems="center">
            <Mui.Grid item xs="auto">
              <Mui.Typography variant="h6">
                {t("tables.livemonitoring.note-title", { customerName: props?.currentRow?.['customer-name'] })}
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs="auto" marginLeft="auto">
              <Mui.IconButton onClick={onClose}>
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogTitle>
        <Mui.DialogContent>
          <Grid item xs={12}>
            <Button
              sx={{ float: "right" }}
              variant={"text"} color={"accent"}
              endIcon={<LibraryAddRounded />} onClick={() => append({ amount: "", date: "", text: "" })}>
              {t('tables.livemonitoring.add-notes')}
            </Button>
          </Grid>

          {fields.map((f, index) =>
            <Grid key={f.id} container spacing={2} marginY={2} padding={3}>
              <Grid container spacing={2} alignItems={'flex-end'}>

                <Grid item md={6} sm={6} xs={12}>
                  <FormTextField
                    name={`notes.${index}.amount`}
                    control={control}
                    props={{
                      variant: "standard", fullWidth: true, label: t('tables.livemonitoring.note-amount'),
                      type: "number",
                      InputProps: {
                        startAdornment:
                          <InputAdornment position="start">
                            <Euro style={{ fontSize: '15px' }} />
                          </InputAdornment>

                      },
                    }}
                  />
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  <FormDatePicker
                    name={`notes.${index}.date`}
                    control={control}
                    props={{
                      label: t('tables.livemonitoring.note-date'),
                    }}

                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} alignItems={'flex-end'}>

                <Grid item xs={12}>
                  <FormTextField
                    name={`notes.${index}.text`}
                    control={control}
                    props={{
                      label: t('tables.livemonitoring.note-text'),
                      fullWidth: true,
                    }}
                  />
                </Grid>
                <Grid item md={1} sm={2} xs={2}>
                                {index >= 0 ?
                                    <Tooltip title={t("tables.livemonitoring.delete")}>
                                        <IconButton color='accent' onClick={() => remove(index)}>
                                            <MuiIcons.Delete />
                                        </IconButton>
                                    </Tooltip>
                                    : null
                                }
                            </Grid>
              </Grid>
            </Grid>
          )}
        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Grid container justifyContent="end" spacing="16px">
            <Mui.Grid item xs="auto" height='48px'>
              <Mui.Button variant="outlined" size="medium" sx={{ maxWidth: '111px', minWidth: "111px", fontWeight: 700 }} onClick={onClose}>
                {t(
                  "tables.threshold.actionButtons.cancel"
                )}
              </Mui.Button>
            </Mui.Grid>
            <Mui.Grid item xs="auto">
              <Mui.Button type="submit" size="medium" sx={{ maxWidth: '111px', minWidth: "111px", fontWeight: 700 }} >
                {t("tables.threshold.actionButtons.save")}
              </Mui.Button>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogActions>

      </form>
    </Mui.Dialog>

  );
};

export default EditNote;



